import { lazy, Suspense } from "react";
import { Switch, Route, Redirect } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { queryClient } from "@/lib/queryClient";
import ChatPage from "@/pages/chat";
import JoinPage from "@/pages/join";
import AdminPage from "@/pages/admin";
import CompleteUpgrade from "@/pages/CompleteUpgrade";
import NotFound from "@/pages/not-found";
import { WelcomeFlow } from "@/components/onboarding/welcome-flow";
import LandingPage from "@/pages/LandingPage";

// Lazy-load non-critical pages for code splitting
const PrivacyPolicy = lazy(() =>
  import("@/pages/privacyPolicy").then((m) => ({ default: m.PrivacyPolicy }))
);
const TermsAndConditions = lazy(() =>
  import("@/pages/termsAndConditions").then((m) => ({ default: m.TermsAndConditions }))
);
const SupportPage = lazy(() =>
  import("@/pages/supportPage").then((m) => ({ default: m.SupportPage }))
);

const WelcomeFlowWrapper = () => {
  const handleComplete = (data: { language: string; name: string }) => {
    console.log('Onboarding completed:', data);
  };

  // Note: logic for completion is likely handled in App.tsx now
  return <WelcomeFlow onComplete={handleComplete} isFirebaseConfigured={true} />;
};

export function Router() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Suspense fallback={<div className="min-h-screen bg-white" />}>
          <Switch>
            {/* Public Routes (kebab-case) */}
            <Route path="/" component={LandingPage} />
            <Route path="/support" component={SupportPage} />
            <Route path="/privacy-policy" component={PrivacyPolicy} />
            <Route path="/terms-and-conditions" component={TermsAndConditions} />

            {/* Legacy redirects (camelCase → kebab-case) */}
            <Route path="/supportPage"><Redirect to="/support" /></Route>
            <Route path="/privacyPolicy"><Redirect to="/privacy-policy" /></Route>
            <Route path="/termsAndConditions"><Redirect to="/terms-and-conditions" /></Route>

            {/* App Routes */}
            <Route path="/app" component={ChatPage} />
            <Route path="/app/chat" component={ChatPage} />
            {/* The open room lives in the URL so back/refresh/deep links work */}
            <Route path="/app/chat/:roomId" component={ChatPage} />
            <Route path="/app/welcome" component={WelcomeFlowWrapper} />
            <Route path="/app/join/:inviteCode" component={JoinPage} />
            <Route path="/app/auth/complete-upgrade">
              <CompleteUpgrade mode="upgrade" />
            </Route>
            <Route path="/app/auth/complete-login">
              <CompleteUpgrade mode="login" />
            </Route>
            <Route path="/app/admin" component={AdminPage} />

            {/* Legacy Redirects */}
            <Route path="/chat"><Redirect to="/app/chat" /></Route>
            <Route path="/chat/*"><Redirect to="/app/chat" /></Route>

            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
